import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useTranslation } from 'react-i18next'

import { mediaApi } from '../../api/endpoints'
import { EmptyState, ErrorState } from '../../components/common/States'
import type { MediaCategory, MediaSeason } from '../../types/api'

const CATEGORIES: MediaCategory[] = [
  'HOUSE',
  'INTERIOR',
  'SAUNA',
  'NATURE',
  'FOOD',
  'DESTINATION',
  'ACTIVITY',
  'OWNER',
  'OTHER',
]

const SEASONS: MediaSeason[] = ['SPRING', 'SUMMER', 'AUTUMN', 'WINTER']

/**
 * The picture library.
 *
 * <p>Organised by what a photograph is OF rather than by where it is used, because that is how
 * someone looks for one: "a winter shot of a sauna" is a search anybody can run, while "the
 * third image on the Koivikko page" is not.
 *
 * <p>Alt text is asked for on the way in rather than offered as an afterthought. A picture with
 * none is invisible to a screen reader and to Google Images, and nobody ever goes back to add it.
 */
export function AdminPicturesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)

  const [filter, setFilter] = useState<MediaCategory | 'ALL'>('ALL')
  const [category, setCategory] = useState<MediaCategory>('HOUSE')
  const [season, setSeason] = useState<MediaSeason | ''>('')
  const [altText, setAltText] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const pictures = useQuery({
    queryKey: ['admin', 'media', filter],
    queryFn: () => mediaApi.list(filter === 'ALL' ? undefined : filter),
  })

  const upload = useMutation({
    mutationFn: (files: File[]) =>
      // Sequential rather than parallel. A phone on a Finnish rural connection uploading six
      // 4 MB photographs at once is how you get six timeouts instead of six pictures.
      files.reduce<Promise<unknown>>(
        (chain, file) =>
          chain.then(() =>
            mediaApi.upload(file, category, season || undefined, altText || undefined),
          ),
        Promise.resolve(),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] })
      setAltText('')
      if (fileInput.current) fileInput.current.value = ''
    },
  })

  const describe = useMutation({
    mutationFn: ({ id, alt }: { id: string; alt: string }) => mediaApi.describe(id, alt),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] }),
  })

  const setVisible = useMutation({
    mutationFn: ({ id, visible }: { id: string; visible: boolean }) =>
      mediaApi.setVisible(id, visible),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => mediaApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'media'] }),
  })

  const copy = (url: string) => {
    void navigator.clipboard?.writeText(url)
    setCopied(url)
    window.setTimeout(() => setCopied(null), 1500)
  }

  const items = pictures.data ?? []
  const missingAlt = items.filter((item) => !item.altText).length

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('admin.pictures')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.picturesIntro')}
      </Typography>

      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: 1, borderColor: 'divider', borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label={t('admin.pictureOf')}
            value={category}
            onChange={(event) => setCategory(event.target.value as MediaCategory)}
            sx={{ minWidth: 180 }}
          >
            {CATEGORIES.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`media.category${option}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t('admin.season')}
            value={season}
            onChange={(event) => setSeason(event.target.value as MediaSeason | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('admin.anySeason')}</MenuItem>
            {SEASONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t(`activities.${option}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            fullWidth
            label={t('admin.altText')}
            value={altText}
            onChange={(event) => setAltText(event.target.value)}
            helperText={t('admin.altTextHelp')}
          />
        </Stack>

        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            if (files.length) upload.mutate(files)
          }}
        />

        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={upload.isPending}
            onClick={() => fileInput.current?.click()}
          >
            {upload.isPending ? t('admin.uploading') : t('admin.choosePictures')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('admin.uploadHint')}
          </Typography>
        </Stack>

        {upload.isPending && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
        {upload.isError && <Box sx={{ mt: 2 }}><ErrorState error={upload.error} /></Box>}
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={t('common.all')}
          onClick={() => setFilter('ALL')}
          color={filter === 'ALL' ? 'primary' : 'default'}
          variant={filter === 'ALL' ? 'filled' : 'outlined'}
        />
        {CATEGORIES.map((option) => (
          <Chip
            key={option}
            label={t(`media.category${option}`)}
            onClick={() => setFilter(option)}
            color={filter === option ? 'primary' : 'default'}
            variant={filter === option ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      {missingAlt > 0 && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 2 }}>
          ⚠️ {t('admin.missingAlt', { count: missingAlt })}
        </Typography>
      )}

      {pictures.isError && <ErrorState error={pictures.error} onRetry={() => void pictures.refetch()} />}
      {!pictures.isLoading && items.length === 0 && (
        <EmptyState icon="📷" title={t('admin.noPictures')} body={t('admin.noPicturesBody')} />
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
        }}
      >
        {items.map((item) => (
          <Paper
            key={item.id}
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}
          >
            <Box
              component="img"
              src={item.url}
              alt={item.altText ?? ''}
              loading="lazy"
              sx={{ display: 'block', width: '100%', aspectRatio: '3 / 2', objectFit: 'cover' }}
            />
            <Box sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Chip size="small" label={t(`media.category${item.category}`)} />
                {item.season && <Chip size="small" variant="outlined" label={t(`activities.${item.season}`)} />}
                {/* Only the hidden state is worth a chip. Saying "on the site" on three
                    hundred cards is noise; saying "hidden" on four is information. */}
                {!item.visible && (
                  <Chip size="small" color="warning" label={t('admin.notOnSite')} />
                )}
              </Stack>

              <TextField
                size="small"
                fullWidth
                variant="standard"
                placeholder={t('admin.altText')}
                defaultValue={item.altText ?? ''}
                onBlur={(event) => {
                  const next = event.target.value.trim()
                  if (next !== (item.altText ?? '')) describe.mutate({ id: item.id, alt: next })
                }}
                error={!item.altText}
              />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {Math.round(item.sizeBytes / 1024)} KB
                </Typography>
                <Stack direction="row">
                  <Tooltip title={item.visible ? t('admin.hideFromSite') : t('admin.showOnSite')}>
                    <IconButton
                      size="small"
                      disabled={setVisible.isPending}
                      onClick={() => setVisible.mutate({ id: item.id, visible: !item.visible })}
                    >
                      {item.visible ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="warning" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={copied === item.url ? t('admin.copied') : t('admin.copyLink')}>
                    <IconButton size="small" onClick={() => copy(item.url)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    aria-label={t('common.delete')}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(item.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  )
}
